import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { asArray, asRecord, runDomainRouterQuery } from './graphql';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type FacultyDashboardData = {
  items: Record<string, unknown>[];
  totalCount: number;
  nextToken: string | null;
};

type FacultyDetailData = {
  faculty: Record<string, unknown> | null;
  assignments: Record<string, unknown>[];
  notes: Record<string, unknown>[];
  communications: Record<string, unknown>[];
};

type FacultyDetailEntry = {
  status: AsyncStatus;
  error: string | null;
  data: FacultyDetailData | null;
};

type FacultyLiveState = {
  dashboard: {
    status: AsyncStatus;
    error: string | null;
    data: FacultyDashboardData;
  };
  detailByFacultyId: Record<string, FacultyDetailEntry>;
};

const initialState: FacultyLiveState = {
  dashboard: {
    status: 'idle',
    error: null,
    data: {
      items: [],
      totalCount: 0,
      nextToken: null,
    },
  },
  detailByFacultyId: {},
};

const createEmptyDetail = (): FacultyDetailEntry => ({
  status: 'idle',
  error: null,
  data: null,
});

export const fetchFacultyDashboard = createAsyncThunk(
  'facultyLive/fetchDashboard',
  async (filter: Record<string, unknown> = {}): Promise<FacultyDashboardData> => {
    const data = asRecord(
      await runDomainRouterQuery('listFaculty', {
        filter,
      })
    );

    return {
      items: asArray<Record<string, unknown>>(data.items),
      totalCount: Number(data.totalCount ?? 0),
      nextToken: data.nextToken == null ? null : String(data.nextToken),
    };
  }
);

export const fetchFacultyDetail = createAsyncThunk(
  'facultyLive/fetchDetail',
  async (facultyId: string) => {
    const normalizedFacultyId = String(facultyId ?? '').trim();

    const [facultyRaw, assignmentsRaw, notesRaw, communicationsRaw] = await Promise.all([
      runDomainRouterQuery('getFaculty', { facultyId: normalizedFacultyId }),
      runDomainRouterQuery('listFacultyAssignments', { facultyId: normalizedFacultyId }),
      runDomainRouterQuery('listFacultyNotes', { facultyId: normalizedFacultyId }),
      runDomainRouterQuery('listFacultyCommunications', { facultyId: normalizedFacultyId }),
    ]);

    const assignments = asArray<Record<string, unknown>>(asRecord(assignmentsRaw).items);
    const notes = asArray<Record<string, unknown>>(asRecord(notesRaw).items);
    const communications = asArray<Record<string, unknown>>(asRecord(communicationsRaw).items);

    return {
      facultyId: normalizedFacultyId,
      detail: {
        faculty: asRecord(facultyRaw),
        assignments,
        notes,
        communications,
      } satisfies FacultyDetailData,
    };
  }
);

const facultyLiveSlice = createSlice({
  name: 'facultyLive',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFacultyDashboard.pending, (state) => {
        state.dashboard.status = 'loading';
        state.dashboard.error = null;
      })
      .addCase(fetchFacultyDashboard.fulfilled, (state, action: PayloadAction<FacultyDashboardData>) => {
        state.dashboard.status = 'succeeded';
        state.dashboard.data = action.payload;
      })
      .addCase(fetchFacultyDashboard.rejected, (state, action) => {
        state.dashboard.status = 'failed';
        state.dashboard.error = action.error.message ?? 'Failed to load faculty dashboard.';
      })
      .addCase(fetchFacultyDetail.pending, (state, action) => {
        const facultyId = action.meta.arg;
        const entry = state.detailByFacultyId[facultyId] ?? createEmptyDetail();
        entry.status = 'loading';
        entry.error = null;
        state.detailByFacultyId[facultyId] = entry;
      })
      .addCase(fetchFacultyDetail.fulfilled, (state, action) => {
        const { facultyId, detail } = action.payload;
        state.detailByFacultyId[facultyId] = {
          status: 'succeeded',
          error: null,
          data: detail,
        };
      })
      .addCase(fetchFacultyDetail.rejected, (state, action) => {
        const facultyId = action.meta.arg;
        const entry = state.detailByFacultyId[facultyId] ?? createEmptyDetail();
        entry.status = 'failed';
        entry.error = action.error.message ?? 'Failed to load faculty detail.';
        state.detailByFacultyId[facultyId] = entry;
      });
  },
});

export const facultyLiveReducer = facultyLiveSlice.reducer;
